// Journal — the append-only, signed and encrypted log of a space, one chain per device.
//
// Acceptance rules for an envelope (local or received, same path):
//   1. right space, well formed;
//   2. next in its author's chain: seq = head + 1 and prev = hash(head) — otherwise duplicate
//      (ignored), gap (held as pending) or fork (rejected);
//   3. its epoch's secret is known (otherwise pending: the rekey may arrive later);
//   4. it decrypts (AES-GCM authenticates it under the space key);
//   5. the author is a member — or this is the genesis: the first device adding itself while no
//      device is known yet; an unknown author is pending (its device.add may come from another
//      device's chain);
//   6. the signature verifies with the author's key; a revoked device is accepted only up to
//      the seq recorded by its revocation;
//   7. the space's policy accepts it (groups: who may remove whom, rename…).
// Key rotations are never rejected for who they cover: devices do not all learn of additions in
// the same order. Instead every device tracks who holds each epoch's secret, and the holder with
// the smallest id hands it to active devices that lack it (space.share). Forgetting someone on
// purpose is thus useless, and nobody is left behind by a race.
// Accepted envelopes are stored, then emitted ('envelope' {envelope, payload, local}).
// Pending ones are retried whenever something new is accepted.

import { hashEnvelope, isEnvelope, openEnvelope, sealEnvelope, verifyEnvelope, type Envelope, type Payload } from './envelope';
import { openSealed, sealFor, type DeviceRecord, type LocalDevice, type SpaceSecrets } from './keys';
import type { Store } from './store';

export type Member = { record: DeviceRecord; addedBy: string; addedAt: number; revokedAfter?: number };
export type IngestResult = 'ok' | 'dup' | 'pending' | 'rejected';
export type Vector = Record<string, number>;

/** Extra acceptance rule supplied by the space (rule 7). Members are those before this envelope. */
export type Policy = (ctx: { author: Member; payload: Payload; journal: Journal }) => Promise<boolean>;

const MAX_PENDING = 20_000;

export class Journal extends EventTarget {
  readonly members = new Map<string, Member>();
  readonly #heads = new Map<string, { seq: number; hash: string }>();
  readonly #pending = new Map<string, Envelope>();
  #chain: Promise<unknown> = Promise.resolve();
  rejected = 0;
  policy: Policy | null = null;

  constructor(
    readonly store: Store,
    readonly device: LocalDevice,
    readonly space: SpaceSecrets,
  ) {
    super();
  }

  async load(): Promise<void> {
    const heads = (await this.store.getKv<[string, { seq: number; hash: string }][]>('heads')) ?? [];
    for (const [author, head] of heads) this.#heads.set(author, head);
    const members = (await this.store.getKv<[string, Member][]>('members')) ?? [];
    for (const [id, m] of members) this.members.set(id, m);
    const secrets = await this.store.getKv<SpaceSecrets>('space');
    if (secrets) Object.assign(this.space, secrets);
  }

  get spaceId(): string {
    return this.space.spaceId;
  }

  /** True once this device appears in the member list (it may then write). */
  get isMember(): boolean {
    const m = this.members.get(this.device.id);
    return !!m && m.revokedAfter === undefined;
  }

  /** This device was a member and has been revoked: it keeps its notes, read-only. */
  get isRevoked(): boolean {
    return this.members.get(this.device.id)?.revokedAfter !== undefined;
  }

  activeMembers(): Member[] {
    return [...this.members.values()].filter((m) => m.revokedAfter === undefined);
  }

  vector(): Vector {
    return Object.fromEntries([...this.#heads].map(([a, h]) => [a, h.seq]));
  }

  /** Envelopes the holder of `remote` does not have, author by author, in order. */
  async missingFor(remote: Vector): Promise<Envelope[]> {
    const out: Envelope[] = [];
    for (const [author, head] of this.#heads) {
      const have = remote[author] ?? 0;
      if (have < head.seq) out.push(...(await this.store.envelopesFrom(author, have + 1)));
    }
    return out;
  }

  /** Founds a new space: this device's first envelope adds itself. */
  genesis(): Promise<Envelope> {
    return this.append({ type: 'device.add', ts: Date.now(), device: publicOf(this.device) });
  }

  /** `epoch` overrides the key epoch (a share must be readable by devices lacking the newest key). */
  append(payload: Payload, { epoch }: { epoch?: number } = {}): Promise<Envelope> {
    return this.#serial(async () => {
      // Only the genesis may be written before membership; a revoked device writes nothing.
      const genesis = payload.type === 'device.add' && this.members.size === 0;
      if (!genesis && !this.isMember) throw new NotMemberError();
      const head = this.#heads.get(this.device.id);
      const e = await sealEnvelope(
        this.device,
        { space: this.spaceId, seq: (head?.seq ?? 0) + 1, prev: head?.hash ?? null, epoch: epoch ?? this.space.epoch },
        this.space.secrets[epoch ?? this.space.epoch],
        payload,
      );
      const result = await this.#accept(e, true);
      if (result !== 'ok') throw new Error(`own envelope ${result}`);
      await this.#retryPending();
      return e;
    });
  }

  ingest(raw: unknown): Promise<IngestResult> {
    return this.#serial(async () => {
      const result = await this.#accept(raw, false);
      if (result === 'ok') await this.#retryPending();
      return result;
    });
  }

  async ingestMany(list: unknown[]): Promise<Record<IngestResult, number>> {
    const counts = { ok: 0, dup: 0, pending: 0, rejected: 0 };
    for (const e of list) counts[await this.ingest(e)]++;
    return counts;
  }

  #serial<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.#chain.then(fn, fn);
    this.#chain = run.catch(() => {});
    return run;
  }

  async #retryPending(): Promise<void> {
    for (let progress = true; progress && this.#pending.size; ) {
      progress = false;
      for (const [key, e] of [...this.#pending]) {
        const r = await this.#accept(e, false);
        if (r !== 'pending') this.#pending.delete(key);
        if (r === 'ok') progress = true;
      }
    }
  }

  #hold(e: Envelope): IngestResult {
    if (this.#pending.size < MAX_PENDING) this.#pending.set(`${e.author}:${e.seq}`, e);
    return 'pending';
  }

  async #accept(raw: unknown, local: boolean): Promise<IngestResult> {
    if (!isEnvelope(raw) || raw.space !== this.spaceId) return this.#reject();
    const e = raw;
    const head = this.#heads.get(e.author);
    if (head && e.seq <= head.seq) return 'dup';
    if (e.seq !== (head?.seq ?? 0) + 1) return this.#hold(e);
    if ((head?.hash ?? null) !== e.prev) return this.#reject();
    const secret = this.space.secrets[e.epoch];
    if (!secret) return this.#hold(e);

    let payload: Payload | null = null;
    for (const candidate of [secret, ...(this.space.alts?.[e.epoch] ?? [])]) {
      try {
        payload = await openEnvelope(e, candidate);
        break;
      } catch {
        // Next candidate: the envelope may use a concurrent rotation's secret.
      }
    }
    // Epoch 0 has a single secret: unreadable is forged. Later epochs may have a concurrent
    // rotation's secret we have not received yet: hold.
    if (!payload) return e.epoch === 0 ? this.#reject() : this.#hold(e);

    const member = this.members.get(e.author);
    const isGenesis = !member && this.members.size === 0 && payload.type === 'device.add' && payload.device.id === e.author && e.seq === 1;
    if (!member && !isGenesis) return this.#hold(e);
    const signPub = member ? member.record.signPub : (payload as Extract<Payload, { type: 'device.add' }>).device.signPub;
    if (!(await verifyEnvelope(e, signPub))) return this.#reject();
    if (member?.revokedAfter !== undefined && e.seq > member.revokedAfter) return this.#reject();
    if (member && this.policy && !(await this.policy({ author: member, payload, journal: this }))) return this.#reject();

    await this.#applyMembership(e, payload);
    await this.store.putEnvelope(e);
    this.#heads.set(e.author, { seq: e.seq, hash: await hashEnvelope(e) });
    await this.store.setKv('heads', [...this.#heads]);
    this.dispatchEvent(new CustomEvent('envelope', { detail: { envelope: e, payload, local } }));
    return 'ok';
  }

  /** Newest epoch below `below` whose secret `device` holds (0 at worst: everyone joined with it). */
  #newestHeld(device: string, below: number): number {
    for (let e = below - 1; e > 0; e--) if (this.#holders(e).has(device) && this.space.secrets[e]) return e;
    return 0;
  }

  #holders(epoch: number): Set<string> {
    return new Set(this.space.holders?.[epoch] ?? []);
  }

  async #addHolders(epoch: number, ids: string[]): Promise<void> {
    const s = this.space;
    s.holders ??= {};
    const set = new Set([...(s.holders[epoch] ?? []), ...ids]);
    s.holders[epoch] = [...set];
    await this.store.setKv('space', s);
  }

  /**
   * Hands the current epoch's secret to active devices that do not hold it. Only the holder with
   * the smallest id does it, so the repair happens once, whoever learned of the gap first.
   */
  async #repairCoverage(): Promise<void> {
    const epoch = this.space.epoch;
    const secret = this.space.secrets[epoch];
    if (epoch === 0 || !secret || !this.isMember) return;
    const holders = this.#holders(epoch);
    const active = this.activeMembers().map((m) => m.record.id);
    const missing = active.filter((id) => !holders.has(id));
    const responsible = active.filter((id) => holders.has(id)).sort()[0];
    if (!missing.length || responsible !== this.device.id) return;
    const sealed: Record<string, { nonce: string; ct: string }> = {};
    for (const id of missing) {
      const s = await sealFor(this.device, this.members.get(id)!.record.dhPub, secret);
      sealed[id] = { nonce: s.nonce, ct: s.ct };
    }
    const author = this.space.rekeyAuthors?.[epoch] ?? this.device.id;
    // Encrypted with the newest epoch every recipient holds: they cannot read the new one yet.
    const readable = Math.min(...missing.map((id) => this.#newestHeld(id, epoch)));
    queueMicrotask(() => void this.append({ type: 'space.share', ts: Date.now(), epoch, author, sealed }, { epoch: readable }).catch(() => {}));
  }

  /**
   * Adopts a secret for an epoch. When two rotations produced the same epoch, the smallest author
   * id wins for writing and the other secret is kept for reading: every device converges alone.
   */
  async adoptSecret(epoch: number, secret: string, author: string): Promise<void> {
    const s = this.space;
    s.alts ??= {};
    s.rekeyAuthors ??= {};
    const current = s.secrets[epoch];
    if (current === undefined) {
      s.secrets[epoch] = secret;
      s.rekeyAuthors[epoch] = author;
    } else if (current !== secret && !(s.alts[epoch] ?? []).includes(secret)) {
      const alts = (s.alts[epoch] ??= []);
      if (author < (s.rekeyAuthors[epoch] ?? '\uffff')) {
        alts.push(current);
        s.secrets[epoch] = secret;
        s.rekeyAuthors[epoch] = author;
      } else alts.push(secret);
    } else return;
    if (epoch > s.epoch) s.epoch = epoch;
    s.holders ??= {};
    s.holders[epoch] = [...new Set([...(s.holders[epoch] ?? []), this.device.id])];
    await this.store.setKv('space', s);
    this.dispatchEvent(new CustomEvent('rekey', { detail: { epoch } }));
  }

  #reject(): IngestResult {
    this.rejected++;
    return 'rejected';
  }

  async #applyMembership(e: Envelope, p: Payload): Promise<void> {
    if (p.type === 'device.add') {
      if (!this.members.has(p.device.id)) this.members.set(p.device.id, { record: p.device, addedBy: e.author, addedAt: p.ts });
      // A device is admitted with the secrets of its time (handed over when it was invited).
      for (const epoch of Object.keys(this.space.secrets).map(Number)) if (epoch <= e.epoch) await this.#addHolders(epoch, [p.device.id]);
      await this.#repairCoverage();
    } else if (p.type === 'device.revoke') {
      const m = this.members.get(p.device);
      if (m && (m.revokedAfter === undefined || p.afterSeq < m.revokedAfter)) m.revokedAfter = p.afterSeq;
    } else if (p.type === 'space.rekey') {
      await this.#addHolders(p.epoch, [e.author, ...Object.keys(p.sealed)]);
      const mine = p.sealed[this.device.id];
      const from = this.members.get(e.author);
      if (mine && from) {
        try {
          await this.adoptSecret(p.epoch, await openSealed(this.device, from.record.dhPub, { from: e.author, ...mine }), e.author);
        } catch {
          // Not for us after all; the envelope itself stays valid.
        }
      }
      await this.#repairCoverage();
    } else if (p.type === 'space.share') {
      await this.#addHolders(p.epoch, Object.keys(p.sealed));
      const mine = p.sealed[this.device.id];
      const from = this.members.get(e.author);
      if (mine && from) {
        try {
          await this.adoptSecret(p.epoch, await openSealed(this.device, from.record.dhPub, { from: e.author, ...mine }), p.author);
        } catch {
          // Not for us.
        }
      }
    } else return;
    await this.store.setKv('members', [...this.members]);
  }
}

export class NotMemberError extends Error {
  constructor() {
    super('this device is not (or no longer) a member of the space');
    this.name = 'NotMemberError';
  }
}

function publicOf(d: LocalDevice): DeviceRecord {
  return { id: d.id, name: d.name, signPub: d.signPub, dhPub: d.dhPub };
}
